import { Suspense, lazy } from 'react';

import type { GrowthChartCanvasProps } from '@/components/prototype/growth-chart-canvas';
import { cn } from '@/lib/utils';

/*
 * The town-growth chart on the company card. This half is the frame - caption,
 * sizing, empty state - and it holds chart.js at arm's length: the canvas is a
 * dynamic import, so the library only downloads for someone who opens a game
 * screen, instead of riding in the entry chunk for every visitor.
 *
 * The frame paints immediately, so the caption and the reserved height are
 * there before the canvas resolves and nothing shifts when it does.
 */
const GrowthChartCanvas = lazy(() => import('@/components/prototype/growth-chart-canvas'));

const DEFAULT_HEIGHT = 72;

const DEFAULT_LABEL = 'Town growth - whole game';

type GrowthChartProps = Omit<GrowthChartCanvasProps, 'label' | 'height'> & {
  label?: string;
  height?: number;
  className?: string;
};

export function GrowthChart({
  label = DEFAULT_LABEL,
  height = DEFAULT_HEIGHT,
  className,
  ...canvas
}: GrowthChartProps) {
  if (canvas.points.length === 0) return null;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Suspense fallback={<div style={{ height }} aria-hidden />}>
        <GrowthChartCanvas {...canvas} label={label} height={height} />
      </Suspense>
      <span className="text-faint text-[10px] uppercase tracking-wide">{label}</span>
    </div>
  );
}
